/**
Service for fetching an image of a virtual borehole.

Return an image of the virtual borehole in PNG format.
*/

import { GeoWebServiceClient } from './GeoWebServiceClient'

/**
 * Web service for fetching an image for a virtual borehole.
 * The following parameters are required: 'X' & 'Y' ( @see setCoordinate ),
 * and 'maxDepth' ( @see setMaxDepth ).
 */
export class VirtualBorehole extends GeoWebServiceClient {
  static get URL (): string { return 'https://data.geo.dk/api/v3/virtualboring' }

  /**
   * Constructor.
   * @param token The Geo auth token.
   */
  constructor (token: string) {
    const defaultParams = {
      geoAreaId: '1',
      format: 'png',
      modelId: '1098',
      width: '334',
      showCopyright: 'true',
      showHeader: 'false'
    }
    super(VirtualBorehole.URL, token, defaultParams)
  }

  /**
   * [Required] Setter for the coordinate.
   * @param coordinate The map coordinate to use in the lookup operation.
   */
  setCoordinate (coordinate: number[]): void {
    if (coordinate.length !== 2) {
      throw new Error('Argument error: coordinate must have exactly two entries.')
    }
    this.addParam('X', coordinate[0].toString())
    this.addParam('Y', coordinate[1].toString())
  }

  /**
   * Setter for hydro model ids.
   * @param hydroModelIds Comma separated list of hydro model ids.
   */
  setHydroModelIds (hydroModelIds: string): void {
    this.addParam('hydromodelid', hydroModelIds)
  }

  /**
   * [Required] Setter for the maximum depth.
   * @param maxDepth The maximum depth of the virtual borehole.
   */
  setMaxDepth (maxDepth: number): void {
    this.addParam('maxDepth', maxDepth.toString())
  }

  /**
   * [Required] Setter for the model id.
   * @param modelId The model id.
   */
  setModelId (modelId: string): void {
    this.addParam('modelId', modelId.toString())
  }

  /**
   * Validates that required params are given.
   */
  private validateParams (): void {
    if (!(this.hasParam('X') && this.hasParam('Y') && this.hasParam('maxDepth'))) {
      throw new Error('Parameter mismatch: The search coordinate and maxDepth params must be set.')
    }
  }

  /**
   * Transforms an ArrayBuffer into a Base64 string.
   * @param buffer the bytearray to transform into a Base64 string
   * @returns a Base64 string
   */
  private arrayBufferToBase64 (buffer: ArrayBuffer): string {
    let binary = ''
    let bytes = [].slice.call(new Uint8Array(buffer))

    bytes.forEach((b) => {
      binary += String.fromCharCode(b)
    })

    return window.btoa(binary)
  }

  /**
   * Calls the web service.
   * @returns A promise bearing the virtual borehole image.
   */
  async fetchVirtualBorehole (): Promise<string> {
    this.validateParams()
    const response: Response = await fetch(this.url(), { headers: { Authorization: `Bearer ${this.token}` } })
    if (!response.ok) {
      throw new Error('Got invalid HTTP response.')
    }
    const buffer = await response.arrayBuffer()
    const base64Flag = 'data:image/jpeg;base64,'
    const imageStr = this.arrayBufferToBase64(buffer)

    return base64Flag + imageStr
  }
}
